import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  createTemplate,
  deleteTemplate,
  sidesQueryOptions,
  templatesQueryOptions,
  updateTemplate,
} from '../../api/admin';
import { ErrorMessage } from '../common/ErrorMessage';
import { LoadingSpinner } from './shared';
import { SidesTab } from './SidesTab';
import { TemplateCreateForm } from './TemplateCreateForm';
import type { TemplatePatch } from './TemplateRow';
import { TemplateRow } from './TemplateRow';
import type { CardTemplate } from './types';

export function TemplatesTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: templates = [], isLoading: loading } = useQuery(
    templatesQueryOptions(),
  );
  const { data: sides = [] } = useQuery(sidesQueryOptions());

  const [showForm, setShowForm] = useState(false);
  const [openSidesPanelId, setOpenSidesPanelId] = useState<string | null>(null);
  const [openDefaultsPanelId, setOpenDefaultsPanelId] = useState<string | null>(
    null,
  );

  const invalidateTemplates = () =>
    void queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] });

  const createMutation = useMutation({
    mutationFn: createTemplate,
    onSuccess: () => {
      invalidateTemplates();
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: TemplatePatch }) =>
      updateTemplate(id, data),
    onSuccess: () => invalidateTemplates(),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteTemplate,
    onSuccess: () => invalidateTemplates(),
  });

  const patchTemplate = async (id: string, data: TemplatePatch) => {
    try {
      await updateMutation.mutateAsync({ id, data });
      return true;
    } catch {
      return false;
    }
  };

  const toggleActive = (tmpl: CardTemplate) =>
    patchTemplate(tmpl.id, { active: !tmpl.active });

  const saveDefaults = async (
    templateId: string,
    defaults: Record<string, string>,
  ) => {
    const ok = await patchTemplate(templateId, { defaults });
    if (ok) {
      setOpenDefaultsPanelId(null);
    }
  };

  // `mutate`, not `mutateAsync`: a failure stays in `deleteMutation.error`
  // and is shown above the list, instead of rejecting a promise nobody awaits.
  const handleDelete = (id: string, name: string) => {
    if (!window.confirm(t('admin.templates.deleteConfirm', { name }))) {
      return;
    }
    deleteMutation.mutate(id);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const showEmpty = templates.length === 0 && !showForm;

  return (
    <div className="space-y-8">
      <SidesTab
        onMutate={() => {
          void queryClient.invalidateQueries({ queryKey: ['admin', 'sides'] });
        }}
      />

      <div className="border-t border-t-rule" />

      <div className="space-y-4">
        <TemplatesHeader
          count={templates.length}
          showForm={showForm}
          onToggleForm={() => setShowForm((v) => !v)}
        />

        {showForm && (
          <TemplateCreateForm
            sides={sides}
            onCreate={async (values) => {
              await createMutation.mutateAsync(values);
            }}
          />
        )}

        {deleteMutation.error && (
          <ErrorMessage message={deleteMutation.error.message} size="xs" />
        )}

        {showEmpty ? (
          <TemplatesEmpty />
        ) : (
          <div className="overflow-hidden border border-rule">
            {templates.map((tmpl, idx) => {
              const isSidesPanelOpen = openSidesPanelId === tmpl.id;
              const isDefaultsPanelOpen = openDefaultsPanelId === tmpl.id;
              return (
                <TemplateRow
                  key={tmpl.id}
                  template={tmpl}
                  first={idx === 0}
                  sides={sides}
                  isSidesPanelOpen={isSidesPanelOpen}
                  isDefaultsPanelOpen={isDefaultsPanelOpen}
                  onToggleSidesPanel={() =>
                    setOpenSidesPanelId(isSidesPanelOpen ? null : tmpl.id)
                  }
                  onToggleDefaultsPanel={() =>
                    setOpenDefaultsPanelId(isDefaultsPanelOpen ? null : tmpl.id)
                  }
                  onCloseSidesPanel={() => setOpenSidesPanelId(null)}
                  onPatch={(data) => patchTemplate(tmpl.id, data)}
                  onToggleActive={() => {
                    void toggleActive(tmpl);
                  }}
                  onDelete={() => handleDelete(tmpl.id, tmpl.name)}
                  onSaveDefaults={(defaults) => {
                    void saveDefaults(tmpl.id, defaults);
                  }}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function TemplatesHeader({
  count,
  showForm,
  onToggleForm,
}: {
  count: number;
  showForm: boolean;
  onToggleForm: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="mb-5 flex items-center justify-between">
      <div>
        <h3 className="text-base font-semibold text-ink">
          {t('admin.templates.title')}
        </h3>
        <p className="mt-0.5 text-sm text-muted">
          {t('admin.templates.count', { count })}
        </p>
      </div>
      <button
        type="button"
        onClick={onToggleForm}
        className={showForm ? 'btn-ghost' : 'btn-blue'}
      >
        {showForm ? (
          t('admin.templates.cancel')
        ) : (
          <>
            <svg
              className="size-3.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            {t('admin.templates.addTemplate')}
          </>
        )}
      </button>
    </div>
  );
}

function TemplatesEmpty() {
  const { t } = useTranslation();
  return (
    <div className="py-16 text-center text-muted-soft">
      <svg
        className="mx-auto mb-3 size-10 opacity-30"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M4 5h16M4 9h16M4 13h10M4 17h6"
        />
      </svg>
      <p className="text-sm">{t('admin.templates.noTemplates')}</p>
    </div>
  );
}
